// /:studioId/waitlist?token=…&action=confirm|cancel
// Opened from the "a spot has opened" email. The email buttons only choose which button
// is highlighted here: nothing changes until the student clicks, so email link scanners
// can't confirm or give up a spot by visiting the link.
import { useEffect, useState } from "react";
import { useStudio } from "../../studios/StudioContext";
import { formatTime } from "../../components/calendarUtils";
import "./waitlist.css";

const longDate = (ymd) =>
  new Date(`${ymd}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const holdText = (iso) =>
  new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function WaitlistOffer() {
  const studio = useStudio();
  const API    = studio.apiBaseUrl;
  const params = new URLSearchParams(window.location.search);
  const token  = params.get("token") || "";
  const wanted = params.get("action") === "cancel" ? "cancel" : "confirm";

  const [offer, setOffer]     = useState(null);
  const [state, setState]     = useState("loading"); // loading | ready | gone | working | booked | cancelled | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) { setState("gone"); return; }
    fetch(`${API}/waitlist/offer?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setState("gone"); setMessage(d.error || ""); return; }
        setOffer(d.offer); setState("ready");
      })
      .catch(() => { setState("error"); setMessage("We couldn't load this offer. Please try again."); });
  }, [API, token]);

  const respond = async (action) => {
    setState("working");
    try {
      const r = await fetch(`${API}/waitlist/respond`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, action }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setState(r.status === 410 ? "gone" : "error"); setMessage(d.error || "Something went wrong."); return; }
      if (d.needsPayment && d.bookUrl) {
        // Their spot stays held while they pay on the booking page.
        window.location.assign(d.bookUrl);
        return;
      }
      if (d.booked)    { setState("booked"); return; }
      if (d.cancelled) { setState("cancelled"); return; }
      setState("error"); setMessage("Something went wrong.");
    } catch {
      setState("error"); setMessage("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="wl-wrap">
      <div className="wl-card">
        <p className="wl-studio">{studio.studioName}</p>

        {state === "loading" && <p className="wl-muted">Loading…</p>}

        {state === "gone" && (
          <>
            <h1 className="wl-title">This offer has ended</h1>
            <p className="wl-text">{message || "The hold has run out or this link was already used."} The spot may have gone to the next person on the waitlist.</p>
            <a className="wl-btn wl-btn--primary" href={`/${studio.studioId}`}>See the schedule</a>
          </>
        )}

        {(state === "ready" || state === "working") && offer && (
          <>
            <h1 className="wl-title">A spot has opened for you</h1>
            <div className="wl-class">
              <span className="wl-class-title">{offer.classTitle}</span>
              <span className="wl-class-meta">{longDate(offer.date)} · {formatTime(offer.time)}{offer.teacher ? ` · ${offer.teacher}` : ""}</span>
            </div>
            <p className="wl-text">We're holding it for you until <strong>{holdText(offer.holdUntil)}</strong>.</p>
            <div className={`wl-actions ${wanted === "cancel" ? "wl-actions--cancel" : ""}`}>
              <button className={`wl-btn ${wanted === "confirm" ? "wl-btn--primary" : "wl-btn--ghost"}`}
                disabled={state === "working"} onClick={() => respond("confirm")}>
                {state === "working" ? "One moment…" : "Confirm my spot"}
              </button>
              <button className={`wl-btn ${wanted === "cancel" ? "wl-btn--danger" : "wl-btn--ghost"}`}
                disabled={state === "working"} onClick={() => respond("cancel")}>
                Cancel — give it to the next person
              </button>
            </div>
            <p className="wl-muted">With a pass or membership, Confirm books you straight in. Otherwise it takes you to pay for the class.</p>
          </>
        )}

        {state === "booked" && (
          <>
            <h1 className="wl-title">You're booked</h1>
            <p className="wl-text">Your spot is confirmed and a confirmation email is on its way.</p>
            <a className="wl-btn wl-btn--primary" href={`/${studio.studioId}/my-bookings`}>My bookings</a>
          </>
        )}

        {state === "cancelled" && (
          <>
            <h1 className="wl-title">Spot passed on</h1>
            <p className="wl-text">You're off the waitlist for this class and we've offered the spot to the next person. You haven't been charged.</p>
            <a className="wl-btn wl-btn--ghost" href={`/${studio.studioId}`}>See the schedule</a>
          </>
        )}

        {state === "error" && (
          <>
            <h1 className="wl-title">Something went wrong</h1>
            <p className="wl-text">{message}</p>
            <button className="wl-btn wl-btn--ghost" onClick={() => window.location.reload()}>Try again</button>
          </>
        )}
      </div>
    </div>
  );
}
